// ============================================================================
// The Supabase client, widened to what mig 295 (RIZ020) added.
//
// WHY THIS EXISTS RATHER THAN AN EDIT TO src/types/database.ts. That file is
// generated by `supabase gen types` and the house rule is never to hand-edit it
// (DECISIONS, "the client was deliberately untyped"). It is regenerated once
// per batch of migrations, after they merge, so a table created today is not in
// it today and `.from('notification_event_types')` does not typecheck.
//
// Widening the SCHEMA GENERIC for exactly the three things this migration
// changed keeps every row fully typed, needs no cast at any call site, and
// keeps ONE client instance - a second createClient means a second GoTrue,
// which is how you get duplicate SIGNED_IN storms and two refresh timers.
//
// When the types are regenerated this intersection becomes a no-op and the
// module can collapse to a re-export.
//
// MIG 480 (RIZ132) WIDENED IT AGAIN, for push: the table `push_subscriptions` (a
// person's devices, read and removed through here) and the function
// `save_push_subscription` (Turn on; the only way a device is ever added). The two
// catalogue columns, push_supported and default_push, ride in on
// NotificationEventTypeRow like the other channels' do.
// ============================================================================
import type { SupabaseClient } from '@supabase/supabase-js'
import { supabase } from '@/lib/supabase'
import type { Database } from '@/types/database'
import type {
  NotificationEventTypeRow,
  NotificationPreferenceRow,
} from '@/lib/notificationPreferences'

// A type alias for the reason NotificationPreferenceRow's comment gives: an interface has no implicit
// index signature, and supabase-js resolves a table whose Row lacks one to `never`.
// Every column of public.push_subscriptions (mig 480), though the browser reads only five of them.
// p256dh and auth are the device's keys; nothing in the browser selects them, and the table's own-row
// RLS is the second line behind that.
export type PushSubscriptionRow = {
  id: string
  user_id: string
  kind: string
  endpoint: string
  p256dh: string
  auth: string
  user_agent: string | null
  created_at: string
  saved_at: string
  last_success_at: string | null
  failure_count: number
}

type NotificationsRow = Database['public']['Tables']['notifications']['Row']
type NotificationsInsert = Database['public']['Tables']['notifications']['Insert']
type NotificationsUpdate = Database['public']['Tables']['notifications']['Update']
type NotificationsRelationships = Database['public']['Tables']['notifications']['Relationships']

// notifications is REPLACED, not intersected. Adding `& { notifications: ... }`
// to the generated table map leaves BOTH entries in play, and supabase-js
// resolves the resulting Row/Insert/Relationships intersection to `never` - a
// failure that surfaces at the CALL site ("Property 'read_at' does not exist on
// type 'never'") and reads as a bug in the query rather than in this file.
// Omit-then-add is what makes the widened entry the only one.
//
// Each entry is spelled out in the generated shape: required on Row,
// optional-where-defaulted on Insert, all optional on Update.
type NotificationSchema = {
  __InternalSupabase: Database['__InternalSupabase']
  public: {
    Views: Database['public']['Views']
    Functions: Database['public']['Functions'] & {
      // Turn on (mig 480). The signed-in person's own act: it keys everything on auth.uid(), takes no
      // user or organization from the caller, and answers 22023 for an address that is not a push
      // service, 42501 'not signed in' with no session and 42501 'MFA required' on a password-only
      // session of an MFA-enrolled member. Returns the device's row id.
      save_push_subscription: {
        Args: { p_auth: string; p_endpoint: string; p_p256dh: string; p_user_agent?: string }
        Returns: string
      }
    }
    Enums: Database['public']['Enums']
    CompositeTypes: Database['public']['CompositeTypes']
    Tables: Omit<Database['public']['Tables'], 'notifications'> & {
      notification_event_types: {
        Row: NotificationEventTypeRow
        Insert: NotificationEventTypeRow
        Update: Partial<NotificationEventTypeRow>
        Relationships: []
      }
      notification_preferences: {
        Row: NotificationPreferenceRow
        Insert: {
          id?: string
          organization_id: string
          user_id: string
          pref_key: string
          channel: NotificationPreferenceRow['channel']
          enabled: boolean
          created_at?: string
          updated_at?: string
        }
        Update: {
          id?: string
          organization_id?: string
          user_id?: string
          pref_key?: string
          channel?: NotificationPreferenceRow['channel']
          enabled?: boolean
          created_at?: string
          updated_at?: string
        }
        Relationships: []
      }
      // A person's devices. The browser may SELECT and DELETE its own rows (own-row RLS plus the AAL2
      // overlay, so an MFA-enrolled member on a password-only session sees none and deletes none, with
      // no error either way) and nothing else: a device is added only by save_push_subscription, and the
      // drain alone writes its health. Insert and Update are `never` so a call that could not work
      // does not compile.
      push_subscriptions: {
        Row: PushSubscriptionRow
        Insert: never
        Update: never
        Relationships: []
      }
      // public.notifications gained in_app in the same migration, and every
      // in-app reader filters on it - the bell's two queries, its realtime
      // handler, its mark-all-read, the sidebar's texting badge and the texting
      // inbox's unread count. They all go through this client until the
      // generated types catch up.
      notifications: {
        Row: NotificationsRow & { in_app: boolean }
        Insert: NotificationsInsert & { in_app?: boolean }
        Update: NotificationsUpdate & { in_app?: boolean }
        Relationships: NotificationsRelationships
      }
    }
  }
}

export const notificationDb = supabase as unknown as SupabaseClient<NotificationSchema>
